<template>
  <div class="recommendation-header" ref="container">
    <v-simple-table
        class="expert-tool__table"
        fixed-header
    >
      <template v-slot:default>
        <tbody>
        <tr>
          <td>
            <div class="expert-tool__table-cell-content">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                      class="expert-tool__table-cell-icon"
                      v-bind="attrs"
                      v-on="on"
                  >mdi-emoticon-dead-outline
                  </v-icon>
                </template>
                <span>{{ $ucfirst($t('expertTool.columnNames.intensity')) }}</span>
              </v-tooltip>
              <div>
                <div>{{ intensityLabel || '' }}</div>
              </div>
            </div>
          </td>
          <td>
            <div class="expert-tool__table-cell-content">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                      class="expert-tool__table-cell-icon"
                      v-bind="attrs"
                      v-on="on"
                  >mdi-chart-line
                  </v-icon>
                </template>
                <span>{{ $ucfirst($t('expertTool.columnNames.frequency')) }}</span>
              </v-tooltip>
              <span class="expert-tool__frequency-label">
                  {{ frequencyLabel || '' }}
                </span>
            </div>
          </td>
          <td>
            <div class="expert-tool__table-cell-content">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                      class="expert-tool__table-cell-icon"
                      v-bind="attrs"
                      v-on="on"
                  >mdi-timer-outline
                  </v-icon>
                </template>
                <span>{{ $ucfirst($t('expertTool.columnNames.sessionDuration')) }}</span>
              </v-tooltip>
              <span>{{ sessionDurationLabel || '' }}</span>
            </div>
          </td>
          <td>
            <div class="expert-tool__table-cell-content">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                      class="expert-tool__table-cell-icon"
                      v-bind="attrs"
                      v-on="on"
                  >mdi-calendar-month-outline
                  </v-icon>
                </template>
                <span>{{ $ucfirst($t('expertTool.columnNames.programDuration')) }}</span>
              </v-tooltip>
              <span
                  :class="{ 'expert-tool__frequency-label': programDurationLabel && programDurationLabel.length < 15 }"
              >{{ programDurationLabel || '' }}</span>
            </div>
          </td>
          <td>
            <div class="expert-tool__table-cell-content">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                      class="expert-tool__table-cell-icon"
                      v-bind="attrs"
                      v-on="on"
                  >mdi-dumbbell
                  </v-icon>
                </template>
                <span>{{ $ucfirst($t('pa.strengthTraining')) }}</span>
              </v-tooltip>
              <span>{{ strengthTrainingLabel || '' }}</span>
            </div>
          </td>
          <td>
            <div class="expert-tool__table-cell-content">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                      class="expert-tool__table-cell-icon"
                      v-bind="attrs"
                      v-on="on"
                  >mdi-run
                  </v-icon>
                </template>
                <span>{{ $ucfirst($t('expertTool.columnNames.additionalTrainingStrategies')) }}</span>
              </v-tooltip>
              <p style="white-space: pre-line">
                <span v-if="additionalTrainingStrategies">{{ additionalTrainingStrategies }}</span>
              </p>
            </div>
          </td>
        </tr>
        </tbody>
      </template>
    </v-simple-table>
    <div
        class="recommendation-header__actions"
        v-if="!isPrevious"
    >
      <expert-tool-undo v-if="isOpen" @confirm="undo"></expert-tool-undo>
      <expert-tool-save @saved="close">
        <template #button="{ save }">
          <v-btn @click.stop="save" color="primary" class="ml-4" fab text small right>
            <eva-icon name="save-outline"></eva-icon>
          </v-btn>
        </template>
      </expert-tool-save>
      <v-btn v-if="isOpen" @click="close" color="primary" class="ml-4" fab text small right>
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-btn v-else @click="open" color="primary" class="ml-4" fab text small right>
        <eva-icon name="edit-2-outline"></eva-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import ExpertToolSave from '../ExpertToolSave';
import ExpertToolUndo from '../ExpertToolUndo.vue';

/**
 * Component RecommendationHeader.vue
 *
 * @module RecommendationHeader
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.9] app/src/APP_DASHBOARD/templates/views/ExpertTool/SportsGoalBoxes/RecommendationHeader.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'RecommendationHeader',
  components: {ExpertToolUndo, ExpertToolSave},
  props: {
    index: {
      type: Number,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    isPrevious: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {}
  },
  computed: {
    heartRate() {
      return this.$store
          .getters[`expertTool/${this.recommendationName}`].heartRate;
    },
    intensityLabel() {
      return this.$store
          .getters[`expertTool/${this.recommendationName}`].intensityLabel;
    },
    extraInfoIntensities() {
      const hiit = this.$t('expertTool.intensity.HIT.short');

      let extraInfoIntensities = this.$store
          .getters[`expertTool/${this.recommendationName}`].extraInfoIntensities;

      if (typeof extraInfoIntensities !== 'undefined' && extraInfoIntensities !== null) {
        return '\r\n • ' + hiit + ' '
            + this.$t('expertTool.columnNames.intensity') + ':\r\n'
            + extraInfoIntensities;
      }
      return null;
    },
    extraInfoSessions() {
      const hiit = this.$t('expertTool.intensity.HIT.short');

      let extraInfoSessions = this.$store
          .getters[`expertTool/${this.recommendationName}`].extraInfoSessions;

      if (typeof extraInfoSessions !== 'undefined' && extraInfoSessions !== null) {
        return '\r\n • ' + hiit + ' '
            + this.$ucfirst(this.$t('sessions')) + ':\r\n'
            + extraInfoSessions;
      }
      return null;
    },
    extraInfoFrequencies() {
      const hiit = this.$t('expertTool.intensity.HIT.short');

      let extraInfoFrequencies = this.$store
          .getters[`expertTool/${this.recommendationName}`].extraInfoFrequencies;

      if (typeof extraInfoFrequencies !== 'undefined' && extraInfoFrequencies !== null) {
        return '\r\n • ' + hiit + ' '
            + this.$t('expertTool.columnNames.frequency') + ':\r\n'
            + extraInfoFrequencies;
      }
      return null;
    },
    frequencyLabel() {
      return this.$store
          .getters[`expertTool/${this.recommendationName}`].frequencyLabel;
    },
    sessionDurationLabel() {
      return this.$store
          .getters[`expertTool/${this.recommendationName}`].sessionDurationLabel;
    },
    programDurationLabel() {
      return this.$store
          .getters[`expertTool/${this.recommendationName}`].programDurationLabel;
    },
    strengthTrainingLabel() {
      return this.$store
          .getters[`expertTool/${this.recommendationName}`].strengthTrainingLabel;
    },
    additionalTrainingStrategies() {
      return this.$store
          .getters[`expertTool/${this.recommendationName}`].additionalTrainingStrategies;
    },
    strengthTrainingDetails() {
      const strengthTrainingDetails = this.$store
          .getters[`expertTool/${this.recommendationName}`].strengthTrainingDetails;
      if (typeof strengthTrainingDetails !== 'undefined' && strengthTrainingDetails !== null) {
        return '\r\n • ' + this.$t('pa.strengthTrainingExercises') + ':\r\n'
            + strengthTrainingDetails;
      }
      return null;
    },
    recommendationName() {
      return this.isPrevious ? 'previousRecommendation' : 'recommendation';
    },
  },
  methods: {
    open() {
      this.$emit('open', this.index);
    },
    close() {
      this.$emit('close', this.index);
    },
    save() {
      this.$emit('save');
    },
    undo() {
      this.$emit('undo');
    },
  },
};
</script>

<style lang="scss">
.recommendation-header {
  display: flex;
  justify-content: space-between;

  table > tbody > tr:hover {
    background: transparent !important;
  }

  .expert-tool__table-cell-icon {
    pointer-events: all;
    cursor: default;
  }
}

.recommendation-header__actions {
  display: flex;
  pointer-events: all;
}
</style>
